// src/components/JobSheetDesigner.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Stack,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Menu,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PreviewIcon from "@mui/icons-material/Preview";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";

const JobSheetDesigner = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [openFieldDialog, setOpenFieldDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [isNewTemplate, setIsNewTemplate] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTemplate, setMenuTemplate] = useState(null);
  const [previewAnswers, setPreviewAnswers] = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [newField, setNewField] = useState({
    type: "",
    label: "",
    required: false,
    options: [],
    conditionalLogic: { enabled: false, showIf: { fieldId: "", operator: "equals", value: "" } },
  });
  const [newOption, setNewOption] = useState("");

  const fieldTypes = [
    { value: "text",      label: "Short Text",          icon: "📝", description: "Single line text input" },
    { value: "textarea",  label: "Long Text",            icon: "📄", description: "Multi-line text area" },
    { value: "number",    label: "Number",               icon: "🔢", description: "Numeric input only" },
    { value: "barcode",   label: "Barcode / Serial No.", icon: "🔲", description: "Scan QR/barcode or type manually" },
    { value: "dropdown",  label: "Dropdown",             icon: "📋", description: "Select from options" },
    { value: "radio",     label: "Radio Buttons",        icon: "⚪", description: "Single choice buttons" },
    { value: "checkbox",  label: "Checkbox",             icon: "☑️", description: "Yes/No toggle" },
    { value: "date",      label: "Date",                 icon: "📅", description: "Date picker" },
    { value: "time",      label: "Time",                 icon: "⏰", description: "Time picker" },
    { value: "photo",     label: "Photo",                icon: "📷", description: "Camera upload" },
    { value: "signature", label: "Signature",            icon: "✍️", description: "Digital signature pad" },
    { value: "section",   label: "Section Header",       icon: "📑", description: "Organize with headers" },
  ];

  useEffect(() => {
    const q = collection(db, "jobSheetTemplates");
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTemplates(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, []);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setFields(template.fields || []);
    setIsNewTemplate(false);
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setTemplateName("");
    setFields([]);
    setIsNewTemplate(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setSnackbar({ open: true, message: "Please enter a template name", severity: "error" });
      return;
    }
    try {
      if (isNewTemplate) {
        await addDoc(collection(db, "jobSheetTemplates"), { name: templateName, fields, createdAt: new Date() });
        setSnackbar({ open: true, message: "Template created successfully", severity: "success" });
      } else {
        await updateDoc(doc(db, "jobSheetTemplates", selectedTemplate.id), { name: templateName, fields });
        setSnackbar({ open: true, message: "Template updated successfully", severity: "success" });
      }
      handleNewTemplate();
    } catch (error) {
      setSnackbar({ open: true, message: "Error saving template: " + error.message, severity: "error" });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await deleteDoc(doc(db, "jobSheetTemplates", templateId));
        setSnackbar({ open: true, message: "Template deleted successfully", severity: "success" });
        if (selectedTemplate?.id === templateId) handleNewTemplate();
      } catch (error) {
        setSnackbar({ open: true, message: "Error deleting template: " + error.message, severity: "error" });
      }
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await addDoc(collection(db, "jobSheetTemplates"), { name: template.name + " (Copy)", fields: template.fields || [], createdAt: new Date() });
      setSnackbar({ open: true, message: "Template duplicated successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Error duplicating template: " + error.message, severity: "error" });
    }
  };

  const handleOpenFieldDialog = (field = null, index = null) => {
    if (field) {
      setEditingField({ ...field, index });
      setNewField({ ...field, conditionalLogic: field.conditionalLogic || { enabled: false, showIf: { fieldId: "", operator: "equals", value: "" } } });
    } else {
      setEditingField(null);
      setNewField({ type: "", label: "", required: false, options: [], conditionalLogic: { enabled: false, showIf: { fieldId: "", operator: "equals", value: "" } } });
    }
    setOpenFieldDialog(true);
  };

  const handleCloseFieldDialog = () => {
    setOpenFieldDialog(false);
    setEditingField(null);
    setNewField({ type: "", label: "", required: false, options: [], conditionalLogic: { enabled: false, showIf: { fieldId: "", operator: "equals", value: "" } } });
    setNewOption("");
  };

  const handleAddField = () => {
    if (!newField.type || !newField.label.trim()) {
      setSnackbar({ open: true, message: "Please fill in field type and label", severity: "error" });
      return;
    }
    if ((newField.type === "dropdown" || newField.type === "radio") && newField.options.length === 0) {
      setSnackbar({ open: true, message: "Please add at least one option", severity: "error" });
      return;
    }
    const fieldData = {
      id: editingField ? editingField.id : `field_${Date.now()}`,
      type: newField.type,
      label: newField.label,
      required: newField.required,
      options: newField.options,
      conditionalLogic: newField.conditionalLogic,
    };
    if (editingField !== null) {
      const updated = [...fields];
      updated[editingField.index] = fieldData;
      setFields(updated);
    } else {
      setFields([...fields, fieldData]);
    }
    handleCloseFieldDialog();
  };

  const handleDeleteField = (index) => setFields(fields.filter((_, i) => i !== index));

  const handleMoveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFields(newFields);
  };

  const handleDuplicateField = (field) => {
    setFields([...fields, { ...field, id: `field_${Date.now()}`, label: field.label + " (Copy)" }]);
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setNewField({ ...newField, options: [...newField.options, newOption.trim()] });
      setNewOption("");
    }
  };

  const handleRemoveOption = (index) => setNewField({ ...newField, options: newField.options.filter((_, i) => i !== index) });

  const shouldShowField = (field, answers) => {
    if (!field.conditionalLogic?.enabled) return true;
    const { showIf } = field.conditionalLogic;
    const dep = answers[showIf.fieldId];
    if (!dep) return false;
    switch (showIf.operator) {
      case "equals": return dep === showIf.value;
      case "notEquals": return dep !== showIf.value;
      case "contains": return String(dep).includes(showIf.value);
      default: return true;
    }
  };

  const getConditionalFields = () => fields.filter((f) => f.type === "dropdown" || f.type === "radio");
  const getConditionalFieldOptions = () => fields.find((f) => f.id === newField.conditionalLogic.showIf.fieldId)?.options || [];

  const handleMenuOpen = (event, template) => { setAnchorEl(event.currentTarget); setMenuTemplate(template); };
  const handleMenuClose = () => { setAnchorEl(null); setMenuTemplate(null); };
  const handlePreviewChange = (fieldId, value) => setPreviewAnswers((prev) => ({ ...prev, [fieldId]: value }));

  const renderPreviewField = (field) => {
    if (!shouldShowField(field, previewAnswers)) return null;

    switch (field.type) {
      case "text":
        return <TextField fullWidth size="small" label={field.label} required={field.required} value={previewAnswers[field.id] || ""} onChange={(e) => handlePreviewChange(field.id, e.target.value)} sx={{ mb: 2 }} />;
      case "textarea":
        return <TextField fullWidth size="small" label={field.label} required={field.required} multiline rows={3} value={previewAnswers[field.id] || ""} onChange={(e) => handlePreviewChange(field.id, e.target.value)} sx={{ mb: 2 }} />;
      case "number":
        return <TextField fullWidth size="small" type="number" label={field.label} required={field.required} value={previewAnswers[field.id] || ""} onChange={(e) => handlePreviewChange(field.id, e.target.value)} sx={{ mb: 2 }} />;
      case "barcode":
        return (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField fullWidth size="small" label={field.label} required={field.required} value={previewAnswers[field.id] || ""} onChange={(e) => handlePreviewChange(field.id, e.target.value)} placeholder="Scan or type serial / barcode..." />
              <Button variant="outlined" size="small" startIcon={<QrCodeScannerIcon />} disabled sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                Scan
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Engineers can scan a QR code or barcode on-site
            </Typography>
          </Box>
        );
      case "dropdown":
        return (
          <FormControl fullWidth size="small" sx={{ mb: 2 }} required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select value={previewAnswers[field.id] || ""} label={field.label} onChange={(e) => handlePreviewChange(field.id, e.target.value)}>
              {field.options.map((opt, i) => <MenuItem key={i} value={opt}>{opt}</MenuItem>)}
            </Select>
          </FormControl>
        );
      case "radio":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{field.label} {field.required && <span style={{ color: "red" }}>*</span>}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {field.options.map((opt, i) => (
                <Chip key={i} label={opt} onClick={() => handlePreviewChange(field.id, opt)} color={previewAnswers[field.id] === opt ? "primary" : "default"} variant={previewAnswers[field.id] === opt ? "filled" : "outlined"} />
              ))}
            </Stack>
          </Box>
        );
      case "checkbox":
        return <FormControlLabel control={<Switch checked={previewAnswers[field.id] || false} onChange={(e) => handlePreviewChange(field.id, e.target.checked)} />} label={field.label + (field.required ? " *" : "")} sx={{ mb: 2 }} />;
      case "date":
        return <TextField fullWidth size="small" type="date" label={field.label} required={field.required} InputLabelProps={{ shrink: true }} value={previewAnswers[field.id] || ""} onChange={(e) => handlePreviewChange(field.id, e.target.value)} sx={{ mb: 2 }} />;
      case "time":
        return <TextField fullWidth size="small" type="time" label={field.label} required={field.required} InputLabelProps={{ shrink: true }} value={previewAnswers[field.id] || ""} onChange={(e) => handlePreviewChange(field.id, e.target.value)} sx={{ mb: 2 }} />;
      case "photo":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{field.label} {field.required && <span style={{ color: "red" }}>*</span>}</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />}>Upload Photo</Button>
          </Box>
        );
      case "signature":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{field.label} {field.required && <span style={{ color: "red" }}>*</span>}</Typography>
            <Paper sx={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.2)" }}>
              <Typography variant="body2" color="text.secondary">✍️ Signature Pad</Typography>
            </Paper>
          </Box>
        );
      case "section":
        return (
          <Box sx={{ mb: 3, mt: 3 }}>
            <Divider sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ color: "primary.main" }}>{field.label}</Typography>
            </Divider>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Avatar sx={{ width: 56, height: 56, mr: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
          <SettingsIcon />
        </Avatar>
        <Box>
          <Typography variant="h4">Job Sheet Designer</Typography>
          <Typography variant="body2" color="text.secondary">Create custom job sheets with conditional logic and live preview</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT — Designer */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Template list */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>📋 Templates</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewTemplate} sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>New Template</Button>
              </Box>
              {templates.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>📝 No templates yet</Typography>
                  <Typography variant="caption" color="text.secondary">Click "New Template" to create your first job sheet</Typography>
                </Box>
              ) : (
                <List sx={{ bgcolor: "rgba(255,255,255,0.02)", borderRadius: 1 }}>
                  {templates.map((template) => (
                    <ListItem key={template.id} button selected={selectedTemplate?.id === template.id} onClick={() => handleSelectTemplate(template)}
                      secondaryAction={<IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, template); }}><MoreVertIcon /></IconButton>}
                      sx={{ borderRadius: 1, mb: 0.5, "&.Mui-selected": { bgcolor: "rgba(102,126,234,0.15)", borderLeft: "3px solid #667eea" } }}>
                      <ListItemText primary={template.name} secondary={`${template.fields?.length || 0} fields`} />
                    </ListItem>
                  ))}
                </List>
              )}
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => { handleDuplicateTemplate(menuTemplate); handleMenuClose(); }}>
                  <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>Duplicate
                </MenuItem>
                <MenuItem onClick={() => { handleDeleteTemplate(menuTemplate.id); handleMenuClose(); }}>
                  <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>Delete
                </MenuItem>
              </Menu>
            </Paper>

            {/* Template editor */}
            {(selectedTemplate || isNewTemplate) && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>{isNewTemplate ? "✨ Create New Template" : "✏️ Edit Template"}</Typography>
                <TextField fullWidth label="Template Name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} sx={{ mb: 3 }} placeholder="e.g., Tracker Installation, CCTV Service" />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1">📝 Fields ({fields.length})</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenFieldDialog()} size="small">Add Field</Button>
                </Box>
                {fields.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4, bgcolor: "rgba(255,255,255,0.02)", borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>📋 No fields yet</Typography>
                    <Typography variant="caption" color="text.secondary">Click "Add Field" to start building your job sheet</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {fields.map((field, index) => {
                      const ft = fieldTypes.find((x) => x.value === field.type);
                      return (
                        <Card key={field.id} sx={{ bgcolor: "rgba(255,255,255,0.02)", "&:hover": { bgcolor: "rgba(255,255,255,0.05)" } }}>
                          <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <DragIndicatorIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                              <Chip label={`#${index + 1}`} size="small" sx={{ width: 40 }} />
                              <Typography sx={{ fontSize: 18, mr: 1 }}>{ft?.icon}</Typography>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2">{field.label}</Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                  <Chip label={ft?.label} size="small" sx={{ fontSize: 10, height: 20 }} />
                                  {field.required && <Chip label="Required" size="small" color="error" sx={{ fontSize: 10, height: 20 }} />}
                                  {field.conditionalLogic?.enabled && <Chip label="Conditional" size="small" color="warning" icon={<AccountTreeIcon sx={{ fontSize: 12 }} />} sx={{ fontSize: 10, height: 20 }} />}
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton size="small" onClick={() => handleMoveField(index, "up")} disabled={index === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => handleMoveField(index, "down")} disabled={index === fields.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => handleOpenFieldDialog(field, index)}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => handleDuplicateField(field)}><ContentCopyIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => handleDeleteField(index)}><DeleteIcon fontSize="small" /></IconButton>
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
                <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                  <Button fullWidth variant="contained" onClick={handleSaveTemplate} sx={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
                    {isNewTemplate ? "Create Template" : "Save Changes"}
                  </Button>
                  <Button fullWidth variant="outlined" onClick={handleNewTemplate}>Cancel</Button>
                </Box>
              </Paper>
            )}
          </Stack>
        </Grid>

        {/* RIGHT — Live Preview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, position: "sticky", top: 16, maxHeight: "calc(100vh - 100px)", overflow: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <PreviewIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6">Live Preview</Typography>
              {fields.length > 0 && (
                <Chip label={`${fields.filter((f) => shouldShowField(f, previewAnswers)).length} visible`} size="small" color="primary" sx={{ ml: "auto" }} />
              )}
            </Box>
            {fields.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h3" sx={{ mb: 2, opacity: 0.3 }}>👁️</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>Live Preview Area</Typography>
                <Typography variant="body2" color="text.secondary">Add fields to see how they'll appear to engineers</Typography>
              </Box>
            ) : (
              <Box>
                <Paper sx={{ p: 3, bgcolor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="h6" sx={{ mb: 3 }}>{templateName || "New Job Sheet"}</Typography>
                  {fields.map((field) => <Box key={field.id}>{renderPreviewField(field)}</Box>)}
                </Paper>
                <Button fullWidth variant="outlined" onClick={() => setPreviewAnswers({})} sx={{ mt: 2 }} startIcon={<DeleteIcon />}>Clear Preview Answers</Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Field Dialog */}
      <Dialog open={openFieldDialog} onClose={handleCloseFieldDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingField ? "✏️ Edit Field" : "➕ Add New Field"}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select value={newField.type} label="Field Type" onChange={(e) => setNewField({ ...newField, type: e.target.value })}>
                {fieldTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{type.icon}</span>
                      <Box>
                        <Typography>{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{type.description}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField fullWidth label="Field Label" value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} placeholder="e.g., Device Serial Number" />

            <FormControlLabel control={<Switch checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} />} label="Required Field" />

            {newField.type === "barcode" && (
              <Alert severity="info" icon={<QrCodeScannerIcon />}>
                Engineers will see a text field with a Scan button. Tapping Scan opens the camera and auto-fills the value when a barcode or QR code is detected. They can also type the value manually.
              </Alert>
            )}

            {(newField.type === "dropdown" || newField.type === "radio") && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField fullWidth size="small" value={newOption} onChange={(e) => setNewOption(e.target.value)} placeholder="Enter option text"
                    onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddOption(); } }} />
                  <Button variant="contained" onClick={handleAddOption}>Add</Button>
                </Box>
                <Stack spacing={1}>
                  {newField.options.map((opt, i) => (
                    <Paper key={i} sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "rgba(255,255,255,0.02)" }}>
                      <Typography>{opt}</Typography>
                      <IconButton size="small" onClick={() => handleRemoveOption(i)}><DeleteIcon fontSize="small" /></IconButton>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Conditional Logic */}
            <Accordion sx={{ bgcolor: "rgba(255,165,0,0.05)", border: "1px solid rgba(255,165,0,0.2)" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccountTreeIcon sx={{ color: "warning.main" }} />
                  <Typography>Conditional Logic (Answer-Specific Flow)</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={<Switch checked={newField.conditionalLogic.enabled} onChange={(e) => setNewField({ ...newField, conditionalLogic: { ...newField.conditionalLogic, enabled: e.target.checked } })} />}
                    label="Enable conditional visibility"
                  />
                  {newField.conditionalLogic.enabled && (
                    <>
                      <Alert severity="info" icon={<AccountTreeIcon />}>This field will only show when the selected condition is met</Alert>
                      <FormControl fullWidth>
                        <InputLabel>Show this field if...</InputLabel>
                        <Select value={newField.conditionalLogic.showIf.fieldId} label="Show this field if..." onChange={(e) => setNewField({ ...newField, conditionalLogic: { ...newField.conditionalLogic, showIf: { ...newField.conditionalLogic.showIf, fieldId: e.target.value } } })}>
                          {getConditionalFields().map((f) => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      {newField.conditionalLogic.showIf.fieldId && (
                        <>
                          <FormControl fullWidth>
                            <InputLabel>Operator</InputLabel>
                            <Select value={newField.conditionalLogic.showIf.operator} label="Operator" onChange={(e) => setNewField({ ...newField, conditionalLogic: { ...newField.conditionalLogic, showIf: { ...newField.conditionalLogic.showIf, operator: e.target.value } } })}>
                              <MenuItem value="equals">Equals</MenuItem>
                              <MenuItem value="notEquals">Not Equals</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl fullWidth>
                            <InputLabel>Value</InputLabel>
                            <Select value={newField.conditionalLogic.showIf.value} label="Value" onChange={(e) => setNewField({ ...newField, conditionalLogic: { ...newField.conditionalLogic, showIf: { ...newField.conditionalLogic.showIf, value: e.target.value } } })}>
                              {getConditionalFieldOptions().map((opt, i) => <MenuItem key={i} value={opt}>{opt}</MenuItem>)}
                            </Select>
                          </FormControl>
                          <Alert severity="success">
                            <Typography variant="body2">
                              <strong>Example:</strong> If user selects "{newField.conditionalLogic.showIf.value}" for "{fields.find((f) => f.id === newField.conditionalLogic.showIf.fieldId)?.label}", then this field will appear.
                            </Typography>
                          </Alert>
                        </>
                      )}
                    </>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFieldDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAddField} sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            {editingField ? "Update Field" : "Add Field"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ open: false, message: "", severity: "success" })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ open: false, message: "", severity: "success" })} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default JobSheetDesigner;